import { Tabs } from "@mui/material";
import InformationFillIcon from "remixicon-react/InformationFillIcon";
import { memo, useState } from "react";
import { Tab, Alert } from "$components";
import JTooltip from "$components/JTooltip/JTooltip";

import { CompanyType } from "$types/CompanyType";
import useStyles from "./DisplayCompanyTabs.styles";
import { useStyles as useStyleProfile } from "./CompanyProfile.styles";
import { getCompanyTypeLabel } from "$utils/typeConstants";

import { UNEDITABLE_CLIENT_MSG, UNEDITABLE_CONTRIBUTOR_MSG } from "./constants";
import { ICompany } from "$services/Entreprises.service";
import { BLTheme, theme } from "$utils/theme/bataillog";

const allowedType = (company, type) => {
  return (
    company?.[
      type === CompanyType.CLIENT ? "wasClientOnce" : "wasContributorOnce"
    ] && !company.companyTypes.includes(type)
  );
};

interface _RenderTabArgs {
  company: ICompany;
  value: CompanyType;
  theme: BLTheme;
  classes: ReturnType<typeof useStyles>;
}

// BEWARE this can't be a component because of the logic of material ui
function RenderTab({ company, value, theme, classes }: _RenderTabArgs) {
  const hasNotTypeAnymore = allowedType(company, value);

  return (
    <Tab
      style={hasNotTypeAnymore ? { color: theme.color.gray3 } : null}
      label={
        <span className={classes.typeTab}>
          {getCompanyTypeLabel(value)}
          {hasNotTypeAnymore && (
            <JTooltip
              title={
                value === CompanyType.CLIENT
                  ? UNEDITABLE_CLIENT_MSG
                  : UNEDITABLE_CONTRIBUTOR_MSG
              }
            >
              <InformationFillIcon size={18} />
            </JTooltip>
          )}
        </span>
      }
      value={value}
      key={value}
    />
  );
}

interface DisplayCompanyTabsProps {
  company: ICompany;
  onChange: (val: CompanyType) => void;
}

export const DisplayCompanyTabs = memo(function DisplayCompanyTabs({
  company,
  onChange, // patch for compatibility
}: DisplayCompanyTabsProps) {
  const allowedTabWithContributor = [
    CompanyType.CLIENT,
    CompanyType.CONTRIBUTOR,
  ];
  const allowedTabWithSupplier = [CompanyType.CLIENT, CompanyType.SUPPLIER];
  let allowedTab = [];
  if (
    company?.companyTypes?.length === 2 &&
    company?.companyTypes?.includes(CompanyType.SUPPLIER)
  ) {
    allowedTab = allowedTabWithSupplier;
  } else {
    allowedTab = allowedTabWithContributor;
  }
  const classes = useStyles();
  const classesProfile = useStyleProfile();
  const [selectedTab, setSelectedTab] = useState(CompanyType.CLIENT);

  if (!allowedTab.every((c) => company?.companyTypes?.includes(c))) {
    return null;
  }

  const handleChangeTab = (_e, newValue: CompanyType) => {
    setSelectedTab(newValue);
    onChange(newValue);
  };

  return (
    <>
      <Tabs
        aria-label="Company types tabs"
        onChange={handleChangeTab}
        TabIndicatorProps={{
          style: { background: theme.color.blueLight, height: 3 },
        }}
        textColor="primary"
        value={selectedTab}
      >
        {allowedTab.map((c) =>
          RenderTab({ value: c, company, theme, classes })
        )}
      </Tabs>

      {allowedType(company, selectedTab) && (
        <Alert className={classesProfile.hasTypeBeforeAlert} severity="warning">
          {selectedTab === CompanyType.CLIENT
            ? UNEDITABLE_CLIENT_MSG
            : UNEDITABLE_CONTRIBUTOR_MSG}
        </Alert>
      )}
    </>
  );
});

//
